import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Coffee, LogOut, Plus, Pencil, Trash2, X, Save, GripVertical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002').replace(/\/$/, '');
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [loading, setLoading] = useState(true);

  // Login form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // Data
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  // Modals
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);

  // Form states
  const [categoryForm, setCategoryForm] = useState({ name: '', order: 0 });
  const [productForm, setProductForm] = useState({
    category_id: '',
    name: '',
    price: '',
    is_available: true,
    order: 0
  });
  const [uploading, setUploading] = useState(false);

  // Check auth on mount
  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyToken = async () => {
    try {
      const res = await fetch(`${API}/admin/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setIsAuthenticated(true);
        fetchData();
      } else {
        localStorage.removeItem('admin_token');
        setToken('');
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('admin_token', data.access_token);
        setToken(data.access_token);
        setIsAuthenticated(true);
        fetchData();
      } else {
        setLoginError(data.detail || 'Giriş başarısız');
      }
    } catch (e) {
      console.error("Giriş Hatası Detayı:", e);
      setLoginError('Bağlantı hatası (Konsolu kontrol edin)');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken('');
    setIsAuthenticated(false);
  };

  const fetchData = async () => {
    try {
      const [catRes, prodRes] = await Promise.all([
        fetch(`${API}/categories`),
        fetch(`${API}/products`)
      ]);
      setCategories(await catRes.json());
      setProducts(await prodRes.json());
    } catch (e) {
      console.error(e);
    }
  };

  const authHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  // Category CRUD
  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({ name: category.name, order: category.order });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', order: categories.length });
    }
    setShowCategoryModal(true);
  };

  const saveCategory = async () => {
    try {
      const url = editingCategory
        ? `${API}/categories/${editingCategory.id}`
        : `${API}/categories`;
      const method = editingCategory ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify(categoryForm)
      });

      if (res.ok) {
        fetchData();
        setShowCategoryModal(false);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Bu kategoriyi ve içindeki tüm ürünleri silmek istediğinize emin misiniz?')) return;
    try {
      await fetch(`${API}/categories/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  // Product CRUD
  const openProductModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        category_id: product.category_id,
        name: product.name,
        price: product.price.toString(),
        is_available: product.is_available,
        order: product.order
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        category_id: categories[0]?.id || '',
        name: '',
        price: '',
        is_available: true,
        order: products.length
      });
    }
    setShowProductModal(true);
  };

  const saveProduct = async () => {
    try {
      const url = editingProduct
        ? `${API}/products/${editingProduct.id}`
        : `${API}/products`;
      const method = editingProduct ? 'PUT' : 'POST';

      const payload = {
        ...productForm,
        price: parseFloat(productForm.price) || 0
      };

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        fetchData();
        setShowProductModal(false);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Bu ürünü silmek istediğinize emin misiniz?')) return;
    try {
      await fetch(`${API}/products/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };


  const getCategoryName = (id) => {
    return categories.find(c => c.id === id)?.name || 'Bilinmiyor';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a2e23] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#d4a84b] border-t-transparent rounded-full" />
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0c0a09] flex items-center justify-center p-6" data-testid="admin-login-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <Coffee className="w-12 h-12 text-[#d4a84b] mx-auto mb-4" />
            <h1 className="font-['Playfair_Display'] text-3xl text-[#f5f0e1]">Yönetim Paneli</h1>
            <p className="text-[#b8c4b8] mt-2">Polje Coffee Co</p>
          </div>

          <form onSubmit={handleLogin} className="bg-[#2d4a3e] p-8 rounded-sm space-y-6">
            {loginError && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-sm text-sm">
                {loginError}
              </div>
            )}

            <div>
              <label className="block text-[#b8c4b8] text-sm mb-2">Kullanıcı Adı</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-[#3d5a4e] border border-[#3d5a4e] text-[#f5f0e1] px-4 py-3 rounded-sm focus:outline-none focus:border-[#d4a84b] transition-colors"
                placeholder="Kullanıcı adınız"
                data-testid="admin-username-input"
              />
            </div>

            <div>
              <label className="block text-[#b8c4b8] text-sm mb-2">Şifre</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#3d5a4e] border border-[#3d5a4e] text-[#f5f0e1] px-4 py-3 rounded-sm focus:outline-none focus:border-[#d4a84b] transition-colors"
                placeholder="Şifreniz"
                data-testid="admin-password-input"
              />
            </div>

            <button
              type="submit"
              className="w-full bg-[#d4a84b] text-[#1a2e23] py-3 rounded-sm font-semibold hover:bg-[#e4b85b] transition-colors"
              data-testid="admin-login-button"
            >
              Giriş Yap
            </button>
          </form>

          <p className="text-center text-[#6b7b6b] text-sm mt-6">
            <a href="/" className="hover:text-[#d4a84b] transition-colors">← Ana Sayfaya Dön</a>
          </p>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#1a2e23]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#2d4a3e] border-b border-[#3d5a4e] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Coffee className="w-8 h-8 text-[#d4a84b]" />
            <span className="font-['Playfair_Display'] text-xl text-[#f5f0e1]">Admin Panel</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/menu" target="_blank" className="text-[#b8c4b8] hover:text-[#d4a84b] text-sm transition-colors">
              Menüyü Görüntüle →
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-[#b8c4b8] hover:text-red-400 transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">Çıkış</span>
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex gap-4 border-b border-[#3d5a4e] mb-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-3 px-2 font-medium transition-colors ${activeTab === 'overview'
              ? 'text-[#d4a84b] border-b-2 border-[#d4a84b]'
              : 'text-[#b8c4b8] hover:text-[#f5f0e1]'
              }`}
          >
            Özet
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`pb-3 px-2 font-medium transition-colors ${activeTab === 'products'
              ? 'text-[#d4a84b] border-b-2 border-[#d4a84b]'
              : 'text-[#b8c4b8] hover:text-[#f5f0e1]'
              }`}
            data-testid="tab-products"
          >
            Ürünler ({products.length})
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`pb-3 px-2 font-medium transition-colors ${activeTab === 'categories'
              ? 'text-[#d4a84b] border-b-2 border-[#d4a84b]'
              : 'text-[#b8c4b8] hover:text-[#f5f0e1]'
              }`}
            data-testid="tab-categories"
          >
            Kategoriler ({categories.length})
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <h2 className="font-['Playfair_Display'] text-2xl text-[#f5f0e1]">Sistem Özeti</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-[#2d4a3e] p-6 rounded-sm border-l-4 border-[#d4a84b]">
                <p className="text-[#b8c4b8] text-sm mb-1">Toplam Ürün</p>
                <div className="text-3xl font-bold text-[#f5f0e1]">{products.length}</div>
              </div>
              <div className="bg-[#2d4a3e] p-6 rounded-sm border-l-4 border-[#d4a84b]">
                <p className="text-[#b8c4b8] text-sm mb-1">Toplam Kategori</p>
                <div className="text-3xl font-bold text-[#f5f0e1]">{categories.length}</div>
              </div>
              <div className="bg-[#2d4a3e] p-6 rounded-sm border-l-4 border-[#d4a84b]">
                <p className="text-[#b8c4b8] text-sm mb-1">Aktif Ürünler</p>
                <div className="text-3xl font-bold text-[#f5f0e1]">{products.filter(p => p.is_available).length}</div>
              </div>
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#f5f0e1]">Ürün Yönetimi</h2>
              <button
                onClick={() => openProductModal()}
                className="flex items-center gap-2 bg-[#d4a84b] text-[#1a2e23] px-4 py-2 rounded-sm font-medium hover:bg-[#e4b85b] transition-colors"
                data-testid="add-product-button"
                disabled={categories.length === 0}
              >
                <Plus className="w-5 h-5" />
                Yeni Ürün
              </button>
            </div>

            {categories.length === 0 ? (
              <div className="bg-[#2d4a3e] p-8 rounded-sm text-center">
                <p className="text-[#b8c4b8]">Önce bir kategori oluşturmalısınız.</p>
              </div>
            ) : products.length === 0 ? (
              <div className="bg-[#2d4a3e] p-8 rounded-sm text-center">
                <p className="text-[#b8c4b8]">Henüz ürün eklenmemiş.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="bg-[#2d4a3e] p-4 rounded-sm flex items-center gap-4"
                    data-testid={`product-item-${product.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[#f5f0e1] font-semibold truncate">{product.name}</h3>
                      <p className="text-[#6b7b6b] text-sm">{getCategoryName(product.category_id)}</p>
                    </div>
                    <div className="text-[#d4a84b] font-semibold whitespace-nowrap">
                      ₺{product.price.toFixed(2)}
                    </div>
                    <div className={`px-2 py-1 rounded text-xs ${product.is_available ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                      {product.is_available ? 'Aktif' : 'Pasif'}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openProductModal(product)}
                        className="p-2 text-[#b8c4b8] hover:text-[#d4a84b] transition-colors"
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => deleteProduct(product.id)}
                        className="p-2 text-[#b8c4b8] hover:text-red-400 transition-colors"
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#f5f0e1]">Kategori Yönetimi</h2>
              <button
                onClick={() => openCategoryModal()}
                className="flex items-center gap-2 bg-[#d4a84b] text-[#1a2e23] px-4 py-2 rounded-sm font-medium hover:bg-[#e4b85b] transition-colors"
                data-testid="add-category-button"
              >
                <Plus className="w-5 h-5" />
                Yeni Kategori
              </button>
            </div>

            {categories.length === 0 ? (
              <div className="bg-[#2d4a3e] p-8 rounded-sm text-center">
                <p className="text-[#b8c4b8]">Henüz kategori eklenmemiş.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {categories.map((category) => (
                  <div
                    key={category.id}
                    className="bg-[#2d4a3e] p-4 rounded-sm flex items-center gap-4"
                    data-testid={`category-item-${category.id}`}
                  >
                    <GripVertical className="w-5 h-5 text-[#6b7b6b]" />
                    <div className="flex-1">
                      <h3 className="text-[#f5f0e1] font-semibold">{category.name}</h3>
                      <p className="text-[#6b7b6b] text-sm">
                        {products.filter(p => p.category_id === category.id).length} ürün
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openCategoryModal(category)}
                        className="p-2 text-[#b8c4b8] hover:text-[#d4a84b] transition-colors"
                        data-testid={`edit-category-${category.id}`}
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => deleteCategory(category.id)}
                        className="p-2 text-[#b8c4b8] hover:text-red-400 transition-colors"
                        data-testid={`delete-category-${category.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Category Modal */}
      <AnimatePresence>
        {showCategoryModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-50"
            onClick={() => setShowCategoryModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#2d4a3e] p-6 rounded-sm w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
              data-testid="category-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-['Playfair_Display'] text-xl text-[#f5f0e1]">
                  {editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori'}
                </h3>
                <button onClick={() => setShowCategoryModal(false)} className="text-[#b8c4b8] hover:text-[#f5f0e1]">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-[#b8c4b8] text-sm mb-2">Kategori Adı</label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full bg-[#3d5a4e] border border-[#3d5a4e] text-[#f5f0e1] px-4 py-3 rounded-sm focus:outline-none focus:border-[#d4a84b]"
                    placeholder="Örn: Kahveler"
                    data-testid="category-name-input"
                  />
                </div>

                <div>
                  <label className="block text-[#b8c4b8] text-sm mb-2">Sıra</label>
                  <input
                    type="number"
                    value={categoryForm.order}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                    className="w-full bg-[#3d5a4e] border border-[#3d5a4e] text-[#f5f0e1] px-4 py-3 rounded-sm focus:outline-none focus:border-[#d4a84b]"
                    data-testid="category-order-input"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCategoryModal(false)}
                  className="flex-1 px-4 py-3 border border-[#3d5a4e] text-[#b8c4b8] rounded-sm hover:bg-[#3d5a4e] transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={saveCategory}
                  className="flex-1 px-4 py-3 bg-[#d4a84b] text-[#1a2e23] rounded-sm font-semibold hover:bg-[#e4b85b] transition-colors flex items-center justify-center gap-2"
                  data-testid="save-category-button"
                >
                  <Save className="w-5 h-5" />
                  Kaydet
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Product Modal */}
      <AnimatePresence>
        {showProductModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-50 overflow-y-auto"
            onClick={() => setShowProductModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#2d4a3e] p-6 rounded-sm w-full max-w-lg my-8"
              onClick={(e) => e.stopPropagation()}
              data-testid="product-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-['Playfair_Display'] text-xl text-[#f5f0e1]">
                  {editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün'}
                </h3>
                <button onClick={() => setShowProductModal(false)} className="text-[#b8c4b8] hover:text-[#f5f0e1]">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                <div>
                  <label className="block text-[#b8c4b8] text-sm mb-2">Kategori</label>
                  <select
                    value={productForm.category_id}
                    onChange={(e) => setProductForm(prev => ({ ...prev, category_id: e.target.value }))}
                    className="w-full bg-[#3d5a4e] border border-[#3d5a4e] text-[#f5f0e1] px-4 py-3 rounded-sm focus:outline-none focus:border-[#d4a84b]"
                    data-testid="product-category-select"
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[#b8c4b8] text-sm mb-2">Ürün Adı</label>
                  <input
                    type="text"
                    value={productForm.name}
                    onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full bg-[#3d5a4e] border border-[#3d5a4e] text-[#f5f0e1] px-4 py-3 rounded-sm focus:outline-none focus:border-[#d4a84b]"
                    placeholder="Örn: Cappuccino"
                    data-testid="product-name-input"
                  />
                </div>


                <div>
                  <label className="block text-[#a8a29e] text-sm mb-2">Fiyat (₺)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={productForm.price}
                    onChange={(e) => setProductForm(prev => ({ ...prev, price: e.target.value }))}
                    className="w-full bg-[#292524] border border-[#292524] text-[#fafaf9] px-4 py-3 rounded-sm focus:outline-none focus:border-[#d6b696]"
                    placeholder="0.00"
                    data-testid="product-price-input"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_available"
                    checked={productForm.is_available}
                    onChange={(e) => setProductForm(prev => ({ ...prev, is_available: e.target.checked }))}
                    className="w-5 h-5 rounded bg-[#3d5a4e] border-[#3d5a4e] text-[#d4a84b] focus:ring-[#d4a84b]"
                    data-testid="product-available-checkbox"
                  />
                  <label htmlFor="is_available" className="text-[#b8c4b8]">Menüde göster</label>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowProductModal(false)}
                  className="flex-1 px-4 py-3 border border-[#3d5a4e] text-[#b8c4b8] rounded-sm hover:bg-[#3d5a4e] transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={saveProduct}
                  className="flex-1 px-4 py-3 bg-[#d4a84b] text-[#1a2e23] rounded-sm font-semibold hover:bg-[#e4b85b] transition-colors flex items-center justify-center gap-2"
                  data-testid="save-product-button"
                >
                  <Save className="w-5 h-5" />
                  Kaydet
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPage;
